import { createContext, useContext, useReducer, useCallback, useEffect, useRef, useState } from "react";
import { authApi, kidsApi, sessionsApi } from "../api/client";
import { flushGuestSessions, getGuestSessions } from "./guestProgress";
import { track } from "../utils/analytics";

const AppContext = createContext(null);

const initialState = {
  user: JSON.parse(localStorage.getItem("user") || "null"),
  token: localStorage.getItem("token") || null,
  kids: [],
  currentKid: JSON.parse(localStorage.getItem("currentKid") || "null"),
  /** False until first GET /kids/ after a persisted token (avoids wrong KidSetup on refresh). */
  kidsBootstrapped: !localStorage.getItem("token"),
  loading: false,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_LOADING": return { ...state, loading: action.payload };
    case "SET_ERROR":   return { ...state, error: action.payload, loading: false };
    case "SET_USER": {
      if (action.payload) localStorage.setItem("user", JSON.stringify(action.payload));
      else localStorage.removeItem("user");
      return { ...state, user: action.payload };
    }
    case "SET_TOKEN": {
      if (action.payload) {
        localStorage.setItem("token", action.payload);
        // Fresh sign-in: was kidsBootstrapped=true when there was no token in storage.
        // Until SET_KIDS runs, kids=[] must not be interpreted as "no kids" (KidSetup create loop).
        return { ...state, token: action.payload, kidsBootstrapped: false };
      }
      localStorage.removeItem("token");
      return { ...state, token: null };
    }
    case "SET_KIDS": {
      const kids = action.payload;
      let { currentKid } = state;
      if (currentKid?.id != null) {
        const match = kids.find((k) => k.id === currentKid.id);
        if (match) {
          currentKid = match;
          localStorage.setItem("currentKid", JSON.stringify(match));
        } else {
          currentKid = null;
          localStorage.removeItem("currentKid");
        }
      }
      return { ...state, kids, currentKid, kidsBootstrapped: true };
    }
    case "KIDS_FETCH_FAILED":
      // Network / 5xx — keep whatever profiles we already have. SET_KIDS([])
      // would drop currentKid (and wipe localStorage), kicking a mid-game
      // child into the first-run KidSetup create flow.
      return { ...state, kidsBootstrapped: true };
    case "SET_CURRENT_KID": {
      if (action.payload) localStorage.setItem("currentKid", JSON.stringify(action.payload));
      else localStorage.removeItem("currentKid");
      return { ...state, currentKid: action.payload };
    }
    case "UPDATE_KID_AFTER_SESSION": {
      const { kidId, stars, gems } = action.payload;
      const updatedKids = state.kids.map(k =>
        k.id === kidId
          ? { ...k, total_stars: k.total_stars + stars, total_gems: (k.total_gems || 0) + gems, games_played: k.games_played + 1 }
          : k
      );
      const updatedCurrent = state.currentKid?.id === kidId
        ? { ...state.currentKid, total_stars: state.currentKid.total_stars + stars, total_gems: (state.currentKid.total_gems || 0) + gems, games_played: state.currentKid.games_played + 1 }
        : state.currentKid;
      if (updatedCurrent) localStorage.setItem("currentKid", JSON.stringify(updatedCurrent));
      return { ...state, kids: updatedKids, currentKid: updatedCurrent };
    }
    case "LOGOUT":
      localStorage.removeItem("token");
      localStorage.removeItem("currentKid");
      localStorage.removeItem("user");
      return { ...initialState, token: null, currentKid: null, user: null, kidsBootstrapped: true };
    default: return state;
  }
}

const A11Y_DEFAULTS = { textScale: 1, highContrast: false, dyslexiaFont: false };

function loadA11y() {
  try {
    return { ...A11Y_DEFAULTS, ...JSON.parse(localStorage.getItem("a11y") || "{}") };
  } catch {
    return A11Y_DEFAULTS;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  // Mastery item_keys the current kid struggles with — games weight question
  // selection toward these (see pickWeighted in db/words.js).
  const [weakItems, setWeakItems] = useState(new Set());

  // Parent/teacher-controlled accessibility prefs, applied app-wide (see effect
  // below) instead of threading props through every inline-styled screen/game.
  const [a11y, setA11yState] = useState(loadA11y);
  const setA11y = useCallback((patch) => {
    setA11yState((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem("a11y", JSON.stringify(next));
      return next;
    });
  }, []);

  // ponytail: blunt global knobs (zoom/filter/an !important stylesheet) rather
  // than per-component theming — the inline-styles-only convention means
  // there's no CSS cascade to hook into, so a plain CSS var on <html> can't
  // override components' hardcoded `style={{fontFamily:...}}`. `zoom` isn't a
  // CSS standard (unsupported on old Firefox); upgrade path is a real
  // rem-based type scale + per-component theming if that gap matters.
  useEffect(() => {
    const root = document.documentElement;
    root.style.zoom = a11y.textScale !== 1 ? String(a11y.textScale) : "";
    root.style.filter = a11y.highContrast ? "contrast(1.5) saturate(1.2)" : "";

    let styleTag = document.getElementById("a11y-font-override");
    if (a11y.dyslexiaFont) {
      if (!styleTag) {
        styleTag = document.createElement("style");
        styleTag.id = "a11y-font-override";
        document.head.appendChild(styleTag);
      }
      styleTag.textContent = "* { font-family: 'Comic Sans MS', 'Comic Neue', sans-serif !important; }";
    } else if (styleTag) {
      styleTag.remove();
    }
  }, [a11y]);

  const refreshMastery = useCallback(async (kidId) => {
    if (!kidId) { setWeakItems(new Set()); return; }
    try {
      const m = await sessionsApi.mastery(kidId);
      setWeakItems(new Set(m.weak_items.map(i => i.item_key)));
    } catch {
      setWeakItems(new Set());
    }
  }, []);

  /** Full list from GET /kids/ — always use this so UI matches DB (order, counts, other devices). */
  const refreshKids = useCallback(async () => {
    try {
      const kids = await kidsApi.list();
      dispatch({ type: "SET_KIDS", payload: kids });
    } catch {
      // Keep the last known kids so a flaky mobile refetch cannot erase the
      // active profile. Do NOT log out on 401 here: login() awaits
      // refreshKids() before React has committed SET_TOKEN, so that first
      // request goes out without a token and 401s — treating it as a stale
      // JWT logged every new user straight back out. A 401 with a real token
      // is already broadcast by api/client.js as auth:unauthorized → LOGOUT.
      dispatch({ type: "KIDS_FETCH_FAILED" });
    }
  }, []);

  // Restore kids from API whenever we have a persisted session (refresh / deep link).
  // Without this, `kids` stays [] while token + currentKid come from localStorage — KidSetup
  // assumes "no kids" and locks into the first-run create flow.
  useEffect(() => {
    if (!state.token) return;
    let cancelled = false;
    (async () => {
      try {
        const kids = await kidsApi.list();
        if (!cancelled) dispatch({ type: "SET_KIDS", payload: kids });
      } catch (e) {
        if (cancelled) return;
        // Stale token: drop it so the user lands on the marketing/login screen
        // instead of being trapped in KidSetup with kids=[].
        if (e?.status === 401) dispatch({ type: "LOGOUT" });
        else dispatch({ type: "KIDS_FETCH_FAILED" });
      }
    })();
    return () => { cancelled = true; };
  }, [state.token]);

  // Any authenticated request returning 401 means the JWT is no longer valid —
  // clear local auth state so the UI returns to the unauthenticated flow.
  useEffect(() => {
    const onUnauthorized = () => dispatch({ type: "LOGOUT" });
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", onUnauthorized);
  }, []);

  // Coming back to the tab — refetch so profiles created elsewhere show up.
  useEffect(() => {
    if (!state.token) return;
    const onVis = () => {
      if (document.visibilityState === "visible") refreshKids();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [state.token, refreshKids]);

  /**
   * login({ idToken, name, email, photo })
   * idToken — Google credential returned by useGoogleLogin / GoogleLogin button.
   *           Sent to the backend for server-side verification when GOOGLE_CLIENT_ID is set.
   * name / email / photo — fallback values used in dev mode (no Google OAuth configured).
   */
  const login = useCallback(async ({ idToken, name = "", email = "", photo = null, role = "parent", ref = undefined } = {}) => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      const data = await authApi.googleLogin(name, email, photo, idToken, role, ref);
      dispatch({ type: "SET_TOKEN", payload: data.access_token });
      dispatch({ type: "SET_USER", payload: { id: data.user_id, name: data.name, email: data.email, role: data.role } });
      await refreshKids();
    } catch (e) {
      dispatch({ type: "SET_ERROR", payload: e.message });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [refreshKids]);

  const logout = useCallback(() => dispatch({ type: "LOGOUT" }), []);

  const loadKids = refreshKids;

  const selectKid = useCallback((kid) => {
    dispatch({ type: "SET_CURRENT_KID", payload: kid });
  }, []);

  // Keep the weak-items set in sync with the active kid
  useEffect(() => {
    if (state.token && state.currentKid?.id) refreshMastery(state.currentKid.id);
    else setWeakItems(new Set());
  }, [state.token, state.currentKid?.id, refreshMastery]);

  const createKid = useCallback(async (data) => {
    const kid = await kidsApi.create(data);
    track("kid_create");
    await refreshKids();
    return kid;
  }, [refreshKids]);

  const updateKid = useCallback(async (id, data) => {
    const updated = await kidsApi.update(id, data);
    await refreshKids();
    return updated;
  }, [refreshKids]);

  const deleteKid = useCallback(async (id) => {
    await kidsApi.delete(id);
    await refreshKids();
  }, [refreshKids]);

  const saveSession = useCallback(async (sessionData) => {
    const kid = state.currentKid;
    if (!kid) return { gems_earned: sessionData.stars * 5 };
    try {
      const result = await sessionsApi.save(kid.id, sessionData);
      const gems = result.gems_earned ?? sessionData.stars * 5;
      dispatch({ type: "UPDATE_KID_AFTER_SESSION", payload: { kidId: kid.id, stars: sessionData.stars, gems } });
      if (sessionData.items?.length) refreshMastery(kid.id); // fire-and-forget refresh
      return result;
    } catch (e) {
      console.warn("Could not save session to server:", e.message);
      return { gems_earned: sessionData.stars * 5 };
    }
  }, [state.currentKid, refreshMastery]);

  // Guest plays banked on a public /games/:id page belong to whichever profile
  // the visitor ends up on. Fires once per kid, on both routes into an account
  // (new profile, or signing back into an existing one). Best-effort: a failure
  // leaves the sessions stored so the next visit can retry.
  const flushedFor = useRef(null);
  useEffect(() => {
    const kidId = state.currentKid?.id;
    if (!state.token || !kidId || flushedFor.current === kidId) return;
    if (!getGuestSessions().length) return;
    flushedFor.current = kidId;
    // flushGuestSessions never rejects — it reports what it could not send —
    // so the retry has to hang off `remaining`, not a .catch that never fires.
    flushGuestSessions(kidId)
      .then(({ posted, remaining }) => {
        if (posted) refreshKids();
        if (remaining) flushedFor.current = null;   // let a later kid switch retry
      });
  }, [state.token, state.currentKid?.id, refreshKids]);

  return (
    <AppContext.Provider value={{ ...state, login, logout, loadKids, refreshKids, selectKid, createKid, updateKid, deleteKid, saveSession, weakItems, refreshMastery, a11y, setA11y }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
